import { createContext, useContext, useState, ReactNode } from 'react';
import { Message } from '@/types/discover';
import useFilteredMessages from '@/hooks/V3/useFilteredMessage';
import { CHATBOT_INITIAL_MESSAGE } from '../_constants/_const';
import { useRef } from 'react';
import { useAppSelector } from '@/redux/store';
interface ChatbotContextProps {
  messages: Message[];
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  setStoredMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  isThinking: boolean;
  setIsThinking: (thinking: boolean) => void;
  input: string;
  setInput: (text: string) => void;
  cancelOngoingRequest: () => void;
  createNewAbortController: () => AbortSignal;
}

const ChatbotContext = createContext<ChatbotContextProps | undefined>(undefined);

export const ChatbotProvider = ({ children }: { children: ReactNode }) => {
  const user = useAppSelector((state) => state.user);

  const { messages, setMessages, setStoredMessages } = useFilteredMessages(
    `ai_chatbot_messages_${user.UserNo}`,
    CHATBOT_INITIAL_MESSAGE,
  );

  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isThinking, setIsThinking] = useState(false);

  const controllerRef = useRef<AbortController | null>(null);

  const cancelOngoingRequest = () => {
    controllerRef.current?.abort();
    controllerRef.current = null;
  };

  const createNewAbortController = () => {
    controllerRef.current?.abort(); // 이전 요청 중단
    controllerRef.current = new AbortController();
    return controllerRef.current.signal;
  };

  const value = {
    messages,
    setMessages,
    setStoredMessages,
    isLoading,
    setIsLoading,
    isThinking,
    setIsThinking,
    input,
    setInput,
    cancelOngoingRequest,
    createNewAbortController,
  };
  return <ChatbotContext.Provider value={value}>{children}</ChatbotContext.Provider>;
};

export const useChatbotContext = () => {
  const context = useContext(ChatbotContext);
  if (!context) {
    throw new Error('useChatbotContext must be used within a ChatbotProvider');
  }
  return context;
};
